import { useEffect, useRef } from 'react';
import { useRouter } from '@blocs.router';
import { signUpQueryParams } from '@minecraft.utils';
import { QUERY_PARAMS, useUserContext } from '@blocs.auth';
import { usePermissions } from '@minecraft.permissions';
import { useBillingPopup } from './BillingPopupContext';
import { PAYWALL_QUERY_PARAMS, PAYWALL_STEPS } from './enums/paywall';
import { PaywallLocation } from './enums/PaywallLocation';

const PAYWALL_UPGRADE = 'UPGRADE';
const UPGRADE_PARAMS_KEY = 'paywall-upgrade';
const PAYWALL_LOCATION = 'paywall-location';

export const usePaywallQueryParams = ({
  step,
  paywallLocation,
}: {
  step: PAYWALL_STEPS;
  paywallLocation?: PaywallLocation;
}) => {
  const { search, pathname, navigate } = useRouter();
  const billingPopup = useBillingPopup();
  const { ability } = usePermissions();
  const { subscription } = useUserContext();

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const searchRef = useRef(search);
  searchRef.current = search;
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  // Deeplink check — fires once on mount
  useEffect(() => {
    const isDeeplink = localStorage.getItem(QUERY_PARAMS.deeplink as string)?.toLocaleLowerCase() === 'true';
    const canUpgrade = ability.can('upgrade', 'subscription');
    if (isDeeplink && !!subscription && canUpgrade) {
      billingPopup.show({
        location: PaywallLocation.DEEPLINK,
      });
    }
    localStorage.removeItem(QUERY_PARAMS.deeplink as string);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync URL params only when step or paywallLocation change explicitly.
  // Uses refs for router state so external navigations don't re-trigger this effect.
  useEffect(() => {
    if (paywallLocation === PaywallLocation.MEMBERSHIP) return;

    const currentSearch = searchRef.current;
    const searchParams = new URLSearchParams(currentSearch);

    const queryParamsMap = {
      [PaywallLocation.SIGNUP]: {
        [PAYWALL_STEPS.INITIAL]: signUpQueryParams.queryParamsSelectMembership,
        [PAYWALL_STEPS.CHECKOUT]: signUpQueryParams.queryParamsMembershipSummary,
        [PAYWALL_STEPS.SUCCESS]: signUpQueryParams.queryParamsMembershipSuccess,
      },
      [PAYWALL_UPGRADE]: {
        [PAYWALL_STEPS.INITIAL]: new URLSearchParams({
          [UPGRADE_PARAMS_KEY]: PAYWALL_QUERY_PARAMS.SELECT_MEMBERSHIP,
        }),
        [PAYWALL_STEPS.CHECKOUT]: new URLSearchParams({
          [UPGRADE_PARAMS_KEY]: PAYWALL_QUERY_PARAMS.MEMBERSHIP_SUMMARY,
        }),
        [PAYWALL_STEPS.SUCCESS]: new URLSearchParams({
          [UPGRADE_PARAMS_KEY]: PAYWALL_QUERY_PARAMS.MEMBERSHIP_SUCCESS,
        }),
      },
    };

    const newParams: URLSearchParams =
      queryParamsMap[paywallLocation === PaywallLocation.SIGNUP ? PaywallLocation.SIGNUP : PAYWALL_UPGRADE][
        step ?? PAYWALL_STEPS.INITIAL
      ];

    newParams.forEach((value, key) => {
      if (!value || !paywallLocation) {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
    });

    if (!paywallLocation) {
      searchParams.delete(PAYWALL_LOCATION);
    } else {
      searchParams.set(PAYWALL_LOCATION, paywallLocation);
    }

    const qs = searchParams.toString();
    const newSearch = qs ? `?${qs}` : '';
    if (newSearch !== currentSearch) {
      navigateRef.current({ to: newSearch || pathnameRef.current });
    }
  }, [step, paywallLocation]); // eslint-disable-line react-hooks/exhaustive-deps
};
